// src/features/projects/hooks/useProject.js

import { useState, useEffect, useCallback } from "react";
import axios from "axios";

export default function useProject({ slug, token, apiUrl }) {
  const [project, setProject] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [membership, setMembership] = useState(null);
  const [effectiveAccess, setEffectiveAccess] = useState({ facilitateOverview: false });
  const [progress, setProgress] = useState({});

  const [loading, setLoading] = useState(true);

  const fetchProject = useCallback(async () => {
    if (!slug || !token) return;

    try {
      setLoading(true);

      const res = await axios.get(
        `${apiUrl}/projects/slug/${slug}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const projectData = res.data.project;
      const programsData = res.data.programs || [];

      setProject(projectData);
      setPrograms(programsData);
      setTracks(res.data.certificationTracks || []);
      setMembership(res.data.membership || null);
      setEffectiveAccess(res.data.effectiveAccess || { facilitateOverview: false });

      const progressMap = {};

      await Promise.all(
        programsData.map(async (program) => {
          try {
            const progressRes = await axios.get(
              `${apiUrl}/programs/${program.programSlug}/progress`,
              {
                params: {
                  projectId: projectData._id
                },
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            );

            const data = progressRes.data || {};

            progressMap[program.programSlug] = {
              completedSteps: data.completedSteps || [],
              completedPhases: data.completedPhases || [],
              availableDocuments: data.availableDocuments || [],
              steps: data.steps || [],
              phases: data.phases || [],
              practiceDayProjection: data.practiceDayProjection || null,
              educationalResults: data.educationalResults || null,
              temporalDelivery: data.temporalDelivery || null,
              learnerPresentation: data.learnerPresentation || null,
              completionPercentage: data.completionPercentage || 0,
              isProgramCompleted: data.isProgramCompleted === true,
              currentStep: data.currentStep || null,
              currentPhase: data.currentPhase || null
            };

          } catch (err) {
            console.error(
              "Error loading progress for program",
              program.programSlug,
              err
            );

            progressMap[program.programSlug] = {
              completedSteps: [],
              completedPhases: [],
              availableDocuments: [],
              steps: [],
              phases: [],
              completionPercentage: 0
            };
          }
        })
      );

      setProgress(progressMap);

    } catch (err) {
      console.error("Error loading project", err);
    } finally {
      setLoading(false);
    }

  }, [slug, token, apiUrl]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  return {
    project,
    programs,
    tracks,
    membership,
    effectiveAccess,
    progress,
    loading,
    refreshProject: fetchProject
  };
}
